import React from 'react';
import { useTranslation } from 'react-i18next';
import { Course, CourseProgress } from '../../../types';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Clock, Monitor, Award, ShieldCheck, Film, BarChart } from 'lucide-react';

interface CourseSidebarProps {
    course: Course;
    isEnrolled?: boolean;
    progress?: CourseProgress | null;
    onEnroll?: () => void;
    onContinue?: () => void;
}

export const CourseSidebar: React.FC<CourseSidebarProps> = ({ course, isEnrolled, progress, onEnroll, onContinue }) => {
    const { t } = useTranslation();
    const isFree = course.price <= 0;

    return (
        <div className="space-y-6">
            <Card className="border-none shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden sticky top-24">
                {/* Price Section */}
                <div className="bg-gray-50 p-6 flex flex-col items-center border-b border-gray-100">
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                        {isFree ? t('course.sidebar.free') : `${course.price.toLocaleString('vi-VN')} VND`}
                    </div>
                    {!isEnrolled ? (
                        <Button
                            size="lg"
                            className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-200"
                            onClick={onEnroll}
                        >
                            {t('course.header.enrollNow')}
                        </Button>
                    ) : (
                        <Button
                            size="lg"
                            className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white rounded-xl shadow-lg shadow-green-200"
                            onClick={onContinue}
                        >
                            {progress && progress.progress >= 100 ? t('course.header.completed') : t('course.header.continue')}
                        </Button>
                    )}
                </div>

                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3 text-gray-700">
                        <Monitor className="w-5 h-5 text-gray-400" />
                        <span className="text-sm font-medium">{t('course.sidebar.access')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-700">
                        <Award className="w-5 h-5 text-gray-400" />
                        <span className="text-sm font-medium">{t('course.sidebar.certificate')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-700">
                        <ShieldCheck className="w-5 h-5 text-gray-400" />
                        <span className="text-sm font-medium">{t('course.sidebar.devices')}</span>
                    </div>

                    <div className="h-px bg-gray-100 my-4" />

                    <h4 className="font-semibold text-gray-900 mb-2">This course includes:</h4>

                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                            <Film className="w-4 h-4" />
                            {t('course.sidebar.videos')}
                        </div>
                        <span className="font-medium text-gray-900">{course.video_count || 0}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                            <Clock className="w-4 h-4" />
                            {t('course.sidebar.duration')}
                        </div>
                        <span className="font-medium text-gray-900">
                            {/* Placeholder for duration calculation if available, otherwise just count */}
                            --
                        </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                            <BarChart className="w-4 h-4" />
                            {t('course.sidebar.level')}
                        </div>
                        <span className="font-medium text-gray-900">
                            {t(`classes.filters.${course.level}`, { defaultValue: course.level })}
                        </span>
                    </div>
                </CardContent>
            </Card>

            {/* Instructor Info */}
            <Card className="border-gray-100 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-lg">{t('course.header.instructor')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold">
                            {course.instructor_name?.charAt(0) || 'A'}
                        </div>
                        <div>
                            <div className="font-medium text-gray-900">{course.instructor_name || 'Admin'}</div>
                            <div className="text-xs text-gray-500">Instructor</div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
